import { useState, useRef } from "react";
import { useI18n } from "@project/lib/use-i18n";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { downloadEntryPassPDF } from "@project/lib/entry-pass-pdf";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@project/components/ui/dialog";
import {
  CheckCircle2,
  Ticket,
  Calendar,
  MapPin,
  Download,
  User,
  Phone,
  Mail,
  Users,
  Upload,
  X,
  Loader2,
} from "lucide-react";
import { formatDate } from "@project/lib/date-format";
import { eventRegistrationService } from "@project/services/admin/event-registration.service";
import { projectToast } from "@project/lib/toast";
 
export interface EventRegistrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: any;
}
 
export function EventRegistrationModal({
  open,
  onOpenChange,
  event,
}: EventRegistrationModalProps) {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
 
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [guests, setGuests] = useState("1");
  const [specialNeeds, setSpecialNeeds] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingRef, setBookingRef] = useState("");
 
  if (!event) {
    return null;
  }
 
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !phone) {
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await eventRegistrationService.create({
        event: event.id,
        full_name: fullName,
        mobile_number: phone,
        email: email || undefined,
        total_attendees: Number(guests) || 1,
        notes: specialNeeds || undefined,
        image: imageFile,
      });

      setBookingRef(
        result.booking_ref ||
          `JVD-EVT-${Math.floor(100000 + Math.random() * 900000)}`
      );
      setSubmitted(true);
      projectToast.success("Successfully registered for the event!");
    } catch (err: any) {
      console.error("Event registration API error:", err);
      // Details are shown automatically by interceptor, but we fallback just in case
      projectToast.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to submit event registration. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFullName("");
    setPhone("");
    setEmail("");
    setGuests("1");
    setSpecialNeeds("");
    setImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        if (!val) {
          handleReset();
        } else {
          onOpenChange(val);
        }
      }}
    >
      <DialogContent className="max-w-lg p-6 sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-primary">
            <Ticket className="h-5 w-5 text-gold" />
            {submitted
              ? t("community.events.rsvpModal.successTitle")
              : t("community.events.rsvpModal.title")}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {submitted
              ? t("community.events.rsvpModal.passNotice")
              : t("community.events.rsvpModal.subtitle")}
          </DialogDescription>
        </DialogHeader>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Event Header Banner summary */}
            <div className="border-gold/30 bg-gold/5 rounded-xl border p-3 text-xs">
              <p className="font-bold text-primary">{event.title}</p>
              <p className="mt-1 text-muted-foreground">
                <Calendar className="mr-1 inline-block h-3.5 w-3.5 text-gold" />
                {event.event_date
                  ? formatDate(event.event_date)
                  : "Upcoming"} | {event.start_time ?? "09:00 AM"}
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold">
                {t("community.events.rsvpModal.fullName")}
              </Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="e.g. Ramesh Shah"
                  className="pl-9"
                />
              </div>
            </div>
 
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold">
                  {t("community.events.rsvpModal.phone")}
                </Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    required
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="pl-9"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold">
                  {t("community.events.rsvpModal.email")}
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">
                {t("community.events.rsvpModal.guests")}
              </Label>
              <div className="relative mt-1">
                <Users className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={guests}
                  onChange={e => setGuests(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">
                {t("community.events.rsvpModal.specialNeeds")}
              </Label>
              <Textarea
                rows={2}
                value={specialNeeds}
                onChange={e => setSpecialNeeds(e.target.value)}
                placeholder="Any special assistance required for elders or family..."
                className="mt-1 text-xs"
              />
            </div>

            {/* Optional Photo/Attachment Upload */}
            <div>
              <Label className="text-xs font-semibold">
                Attachment / Photo (Optional)
              </Label>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={e => setImageFile(e.target.files?.[0] ?? null)}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 border-dashed border-[#DCD5C6] text-xs"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  <Upload className="mr-1.5 h-3.5 w-3.5 text-gold" />
                  {imageFile ? "Change Photo" : "Upload Photo"}
                </Button>
                {imageFile && (
                  <div className="flex max-w-[200px] items-center gap-1.5 rounded-md bg-[#F4EFE6] px-2 py-1 text-xs text-[#4A3E31]">
                    <span className="truncate text-xs">{imageFile.name}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = "";
                        }
                      }}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="hover:bg-gold/90 min-w-[100px] bg-gold text-gold-foreground"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  t("community.events.rsvpModal.submit")
                )}
              </Button>
            </div>
          </form>
        ) : (
          /* Confirmation Ticket Pass Screen */
          <div className="mt-4 space-y-4 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600">
              <CheckCircle2 className="h-10 w-10" />
            </div>

            <div className="border-gold/60 bg-gold/5 rounded-2xl border-2 border-dashed p-5 text-left">
              <div className="border-gold/30 mb-3 flex items-center justify-between border-b pb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-gold">
                  Jivandhara Entry Pass
                </span>
                <span className="rounded bg-primary px-2 py-0.5 text-[10px] font-bold text-white">
                  {bookingRef}
                </span>
              </div>
              <h4 className="text-base font-extrabold text-primary">
                {event.title}
              </h4>
              <p className="mt-1 text-xs text-muted-foreground">
                <Calendar className="mr-1 inline-block h-3.5 w-3.5 text-gold" />
                {event.event_date
                  ? formatDate(event.event_date)
                  : "Upcoming"} | {event.start_time ?? "09:00 AM"}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                <MapPin className="mr-1 inline-block h-3.5 w-3.5 text-gold" />
                {event.venue ?? "Jivandhara Campus, Palitana"}
              </p>

              <div className="border-gold/30 mt-4 grid grid-cols-2 gap-2 border-t pt-3 text-xs">
                <div>
                  <span className="block text-[10px] text-muted-foreground">
                    Primary Guest
                  </span>
                  <span className="font-semibold">{fullName}</span>
                </div>
                <div>
                  <span className="block text-[10px] text-muted-foreground">
                    Total Attendees
                  </span>
                  <span className="font-semibold">{guests} Person(s)</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() =>
                  downloadEntryPassPDF({
                    bookingRef,
                    eventTitle: event.title,
                    eventDate: event.event_date,
                    startTime: event.start_time,
                    venue: event.venue || "Jivandhara Campus, Palitana",
                    fullName,
                    guests,
                  })
                }
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                {t("community.events.rsvpModal.downloadPass")}
              </Button>
              <Button
                onClick={handleReset}
                className="bg-primary text-primary-foreground"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
